'use client';

/**
 * @file This module defines a player store and related utilities for managing media player instances.
 */

import type { VidstackPlayerProps } from '@/components/player/components/vidstack-player';
import { MediaPlayerInstance } from '@vidstack/react';
import { createRef, RefObject } from 'react';
import { ControlPosition } from 'react-draggable';
import { createHtmlPortalNode, HtmlPortalNode } from 'react-reverse-portal';
import { createStore } from 'zustand-x';

/**
 * Represents a player instance with its associated properties and references.
 */
export type PlayerInstance = {
  ref: RefObject<MediaPlayerInstance>;
  props: VidstackPlayerProps;
  node: HtmlPortalNode;
  pathname: string;
};

/**
 * Defines the structure of the player store.
 */
export type PlayerStore = {
  playerInstancesMap: Record<string, PlayerInstance>;
  miniPlayerEntityId: string | undefined;
  miniPlayerPosition: ControlPosition | undefined;
  settings: PlayerSettings;
};

export type PlayerSettings = {
  volume: number;
  playbackRate: number;
};

/**
 * Zustand store for managing player instances and their states.
 * Includes methods for initializing, updating, and resetting player instances.
 */
export const playerStore = createStore('player')<PlayerStore>(
  {
    playerInstancesMap: {},
    miniPlayerEntityId: undefined,
    miniPlayerPosition: undefined,
    settings: {
      volume: 0,
      playbackRate: 1
    }
  },
  {
    persist: {
      enabled: true,
      partialize: (state) =>
        ({
          miniPlayerPosition: state.miniPlayerPosition,
          settings: state.settings
        }) as PlayerStore
    }
  }
)
  .extendSelectors((_, get) => ({
    /**
     * Retrieves a player instance by its key.
     * @param key - The key of the player instance.
     * @returns The player instance or undefined if not found.
     */
    playerInstance: (key: string): PlayerInstance | undefined => {
      return get.playerInstancesMap()[key];
    },
    /**
     * Retrieves the mini player instance.
     * @returns The mini player instance or undefined if not set.
     */
    miniPlayerInstance: (): PlayerInstance | undefined => {
      const miniPlayerEntityId = get.miniPlayerEntityId();
      return miniPlayerEntityId
        ? get.playerInstancesMap()[miniPlayerEntityId]
        : undefined;
    }
  }))
  .extendActions((set, get, api) => ({
    /**
     * Initializes a player instance.
     * @param entityId - The ID of the player entity.
     * @param props - The properties for the player.
     * @param pathname - The pathname associated with the player.
     */
    initPlayer: (
      entityId: string,
      props: VidstackPlayerProps,
      pathname: string
    ) => {
      const playerInstancesMap = get.playerInstancesMap();
      if (!playerInstancesMap[entityId]) {
        playerInstancesMap[entityId] = {
          node: createHtmlPortalNode(),
          ref: createRef<MediaPlayerInstance>(),
          props,
          pathname
        };
        api.set.playerInstancesMap(Object.assign({}, playerInstancesMap));
      } else {
        playerInstancesMap[entityId].props = props ?? {};
        api.set.playerInstancesMap(Object.assign({}, playerInstancesMap));
      }
    },
    /**
     * Updates the properties of a player instance.
     * @param entityId - The ID of the player entity.
     * @param props - The updated properties for the player.
     */
    updatePlayerProps: (entityId: string, props: VidstackPlayerProps) => {
      const playerInstancesMap = get.playerInstancesMap();
      if (playerInstancesMap[entityId]) {
        playerInstancesMap[entityId].props = props;
        api.set.playerInstancesMap(Object.assign({}, playerInstancesMap));
      }
    },
    /**
     * Pause all other players except for current player
     * @param currentEntityId - The ID of the player entity.
     */
    pauseOtherPlayers: (currentEntityId: string) => {
      const playerInstancesMap = get.playerInstancesMap();
      for (const [entityId, instance] of Object.entries(playerInstancesMap)) {
        if (entityId !== currentEntityId) {
          const player = instance.ref.current;
          if (player?.state?.playing) {
            player.pause();
          }
        }
      }
    },
    /**
     * Pause player with given entityId
     * @param entityId - The ID of the player entity.
     */
    pausePlayer: (entityId: string) => {
      const playerInstancesMap = get.playerInstancesMap();
      const player = playerInstancesMap[entityId]?.ref.current;
      if (player?.state?.playing) {
        player.pause();
      }
    },
    /**
     * Resets the player store and clears storage.
     */
    reset: () => {
      set.miniPlayerEntityId(undefined);
      set.playerInstancesMap({});
    }
  }));
